import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  AutoGraph as AutoGraphIcon,
  CalendarMonth as CalendarMonthIcon,
  Groups2 as Groups2Icon,
  Payments as PaymentsIcon,
  RequestQuote as RequestQuoteIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { apiUrl } from '../config/api';
import AppMonthField from '../components/inputs/AppMonthField';
import {
  BreakdownList,
  EmptyState,
  fmtMoney,
  LoadingOverlay,
  MiniStat,
  StatCard,
  TableSection,
} from '../components/reports/ReportUi';
import {
  formatSriLankaDateLabel,
  formatSriLankaMonthLabel,
  getSriLankaMonthValue,
} from '../lib/sriLankaTime';

const API_URL = apiUrl('reports_dashboard');

export default function ReportsDashboard() {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [salaryMonth, setSalaryMonth] = useState(() => getSriLankaMonthValue());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedMonthLabel = useMemo(() => formatSriLankaMonthLabel(salaryMonth), [salaryMonth]);

  const dailyProfitTotals = useMemo(() => {
    const rows = Array.isArray(data?.daily_profit_rows) ? data.daily_profit_rows : [];
    return rows.reduce((acc, row) => {
      acc.gross_profit += Number(row.gross_profit || 0);
      acc.net_profit += Number(row.net_profit || 0);
      acc.advance_total += Number(row.advance_total || 0);
      acc.bank_charge += Number(row.bank_charge || 0);
      acc.operating_expenses += Number(row.operating_expenses || 0);
      acc.purchase_expenses += Number(row.purchase_expenses || 0);
      return acc;
    }, {
      gross_profit: 0,
      net_profit: 0,
      advance_total: 0,
      bank_charge: 0,
      operating_expenses: 0,
      purchase_expenses: 0,
    });
  }, [data?.daily_profit_rows]);

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        month: salaryMonth,
      });

      const response = await fetch(`${API_URL}?${params.toString()}`);
      const payload = await response.json();

      if (!response.ok || payload?.status === 'error') {
        throw new Error(payload?.message || 'Failed to load reports');
      }

      setData(payload);
    } catch (err) {
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [salaryMonth]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const salesSummary = data?.sales_summary || null;
  const salarySummary = data?.salary_summary || null;
  const salaryBreakdownSummary = data?.salary_breakdown_summary || null;
  const tipServiceSummary = data?.tip_service_summary || null;
  const organizationFundSummary = data?.organization_fund_summary || null;
  const monthProfitSummary = data?.month_profit_summary || null;
  const dailyRows = Array.isArray(data?.daily_sales_rows) ? data.daily_sales_rows : [];
  const monthlyRows = Array.isArray(data?.monthly_sales_rows) ? data.monthly_sales_rows : [];
  const profitRows = Array.isArray(data?.daily_profit_rows) ? data.daily_profit_rows : [];
  const salaryRows = Array.isArray(data?.salary_rows) ? data.salary_rows : [];
  const displayedMonthLabel = data?.filters?.month_label || selectedMonthLabel;
  const timezoneLabel = data?.filters?.timezone || 'Asia/Colombo';
  const headerCellSx = {
    fontWeight: 700,
    whiteSpace: 'nowrap',
    bgcolor: alpha(theme.palette.primary.main, 0.035),
  };
  const stripedRowSx = {
    '&:nth-of-type(odd)': {
      bgcolor: alpha(theme.palette.primary.main, 0.018),
    },
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 4,
          color: '#fff',
          background: 'linear-gradient(135deg, #11263f 0%, #1c5276 58%, #2b8e8a 100%)',
          boxShadow: '0 30px 60px rgba(13, 32, 52, 0.22)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 220,
            height: 220,
            borderRadius: '50%',
            top: -70,
            right: -40,
            bgcolor: 'rgba(255,255,255,0.08)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 130,
            height: 130,
            borderRadius: '50%',
            bottom: -25,
            left: '38%',
            bgcolor: 'rgba(255,255,255,0.06)',
          }}
        />

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={3}
          justifyContent="space-between"
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Box sx={{ maxWidth: 760 }}>
            <Typography variant="overline" sx={{ letterSpacing: 1.5, opacity: 0.85 }}>
              Monthly Report Center
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1, mb: 1.25 }}>
              Business reports for {displayedMonthLabel}
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 680, opacity: 0.9 }}>
              Review daily sales, monthly totals, profit movement, and salary amounts without advance deductions from one dashboard.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="flex-start">
            <Chip
              label={`Viewing ${displayedMonthLabel}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 700 }}
            />
            <Chip
              label={`${salesSummary?.day_count || 0} active days`}
              sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 700 }}
            />
            <Chip
              label={`${salesSummary?.card_breakdown?.length || 0} payment channels`}
              sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 700 }}
            />
            <Chip
              label={timezoneLabel}
              sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 700 }}
            />
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2.25,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
          <AppMonthField
            label="Report Month"
            size="small"
            value={salaryMonth}
            onChange={(e) => setSalaryMonth(e.target.value)}
            sx={{ minWidth: 220 }}
            helperText="MM/YYYY"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarMonthIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Reports reload automatically
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Change the month and the dashboard will refresh every tab for that selection.
            </Typography>
          </Box>

          <Chip
            color={loading ? 'info' : 'success'}
            label={loading ? `Loading ${selectedMonthLabel}` : `Showing ${displayedMonthLabel}`}
            sx={{ fontWeight: 700 }}
          />
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {!error ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          All report dates and month totals follow Sri Lankan time ({timezoneLabel}).
        </Alert>
      ) : null}

      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <LoadingOverlay open={loading} monthLabel={selectedMonthLabel} />

        {!data ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              display: 'grid',
              placeItems: 'center',
              minHeight: 280,
            }}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress size={38} thickness={4.2} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Preparing {selectedMonthLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fetching sales, profit, and salary data for the selected month.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <StatCard
                title="Total Sales"
                value={`Rs ${fmtMoney(salesSummary?.total_sales)}`}
                caption={`Gross sales including credit sales for ${displayedMonthLabel}`}
                icon={<AssessmentIcon />}
                tone="primary"
              />
              <StatCard
                title="Cash Payments"
                value={`Rs ${fmtMoney(salesSummary?.cash_payments)}`}
                caption={`Only cash received: sales - online - card - credit`}
                icon={<PaymentsIcon />}
                tone="success"
              />
              <StatCard
                title="Total Salaries"
                value={`Rs ${fmtMoney(monthProfitSummary?.total_salaries)}`}
                caption={`Bonuses: Rs ${fmtMoney(monthProfitSummary?.bonus_total)} | Penalties: Rs ${fmtMoney(monthProfitSummary?.penalty_total)}`}
                icon={<Groups2Icon />}
                tone="info"
              />
              <StatCard
                title="Month Balance"
                value={`Rs ${fmtMoney(monthProfitSummary?.balance_profit)}`}
                caption={`Recognized sales: Rs ${fmtMoney(monthProfitSummary?.recognized_sales)}`}
                icon={<TrendingUpIcon />}
                tone="warning"
              />
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: `linear-gradient(180deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
              }}
            >
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} justifyContent="space-between">
                <Box sx={{ maxWidth: 430 }}>
                  <Typography variant="overline" sx={{ letterSpacing: 1.1, color: 'text.secondary' }}>
                    Snapshot
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
                    Selected month analytics
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Month balance uses gross sales minus credit sales, then adds settlements and other income, then subtracts bank charge, expenses, purchases, salaries, and advance payments.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', flex: 1 }}>
                  <MiniStat label="Recognized Sales" value={`Rs ${fmtMoney(monthProfitSummary?.recognized_sales)}`} tone="success" />
                  <MiniStat label="Credit Sales Deducted" value={`Rs ${fmtMoney(monthProfitSummary?.credit_sales)}`} tone="warning" />
                  <MiniStat label="Card Payments" value={`Rs ${fmtMoney(salesSummary?.card_payments)}`} tone="info" />
                  <MiniStat label="Online Payments" value={`Rs ${fmtMoney(salesSummary?.online_sales)}`} tone="warning" />
                  <MiniStat label="Bank Charge" value={`Rs ${fmtMoney(monthProfitSummary?.bank_charge)}`} tone="error" />
                  <MiniStat label="Expenses" value={`Rs ${fmtMoney(monthProfitSummary?.operating_expenses)}`} tone="warning" />
                  <MiniStat label="Purchases" value={`Rs ${fmtMoney(monthProfitSummary?.purchase_expenses)}`} tone="error" />
                  <MiniStat label="Advance Payments" value={`Rs ${fmtMoney(monthProfitSummary?.advance_payments)}`} tone="error" />
                </Box>
              </Stack>

              <Box sx={{ mt: 2.2, pt: 2.2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Card Payment Types
                    </Typography>
                    <BreakdownList items={salesSummary?.card_breakdown} tone="info" />
                  </Box>

                  <Button
                    component={RouterLink}
                    to="/profit-report"
                    variant="contained"
                    sx={{ borderRadius: 999, px: 2.2, flexShrink: 0 }}
                  >
                    Open Monthly Profit Page
                  </Button>
                </Stack>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: `linear-gradient(180deg, ${alpha(theme.palette.success.main, 0.06)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
              }}
            >
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} justifyContent="space-between">
                <Box sx={{ maxWidth: 460 }}>
                  <Typography variant="overline" sx={{ letterSpacing: 1.1, color: 'text.secondary' }}>
                    Salary And Fund Analysis
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
                    Tips, service charge, bonuses, penalties, and fund split
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tips are analyzed as salary share. Service charge is analyzed as 50% salary share and 50% organization fund, with any removed salary shares and punishment half-day balances also moved to the fund.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', flex: 1 }}>
                  <MiniStat label="Tips To Salary" value={`Rs ${fmtMoney(tipServiceSummary?.tips_to_salary)}`} tone="success" />
                  <MiniStat label="Service To Salary" value={`Rs ${fmtMoney(tipServiceSummary?.service_to_salary)}`} tone="info" />
                  <MiniStat label="Bonuses" value={`Rs ${fmtMoney(salaryBreakdownSummary?.bonus_total)}`} tone="success" />
                  <MiniStat label="Penalties" value={`Rs ${fmtMoney(salaryBreakdownSummary?.penalty_total)}`} tone="warning" />
                  <MiniStat label="Punish Half Fund" value={`Rs ${fmtMoney(organizationFundSummary?.punishment_half_day)}`} tone="warning" />
                  <MiniStat label="Org Fund Total" value={`Rs ${fmtMoney(organizationFundSummary?.total)}`} tone="warning" />
                  <MiniStat label="Service 50% Fund" value={`Rs ${fmtMoney(organizationFundSummary?.service_charge_org_50)}`} tone="info" />
                </Box>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 0.8,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              }}
            >
              <Tabs
                value={tab}
                onChange={(_, next) => setTab(next)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    minHeight: 46,
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: 'none',
                  },
                }}
              >
                <Tab icon={<AssessmentIcon fontSize="small" />} iconPosition="start" label={`Daily Sales (${dailyRows.length})`} />
                <Tab icon={<AutoGraphIcon fontSize="small" />} iconPosition="start" label={`Monthly Sales (${monthlyRows.length})`} />
                <Tab icon={<TrendingUpIcon fontSize="small" />} iconPosition="start" label={`Daily Profit (${profitRows.length})`} />
                <Tab icon={<Groups2Icon fontSize="small" />} iconPosition="start" label={`Salaries (${salaryRows.length})`} />
              </Tabs>
            </Paper>

            {tab === 0 ? (
              <TableSection
                eyebrow="Daily View"
                title="Full Sale Report Per Day"
                description="Daily totals from the bill book in Sri Lanka time, with cash derived after removing online, card, and credit sales."
              >
                {dailyRows.length === 0 ? (
                  <EmptyState
                    title="No daily sales found"
                    description="There are no daily sales rows recorded for the selected month."
                  />
                ) : (
                  <TableContainer sx={{ maxHeight: 680 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellSx}>Date</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Total Sales</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Cash</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Card</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Online</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Credit</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Tips</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Service</TableCell>
                          <TableCell sx={headerCellSx}>Card Types</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dailyRows.map((row) => (
                          <TableRow key={row.date} hover sx={stripedRowSx}>
                            <TableCell sx={{ fontWeight: 700 }}>{formatSriLankaDateLabel(row.date)}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.total_sales)}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.cash_payments)}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.card_payments)}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.online_sales)}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.credit_sales)}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.total_tips)}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.total_service_charges)}</TableCell>
                            <TableCell sx={{ minWidth: 300 }}>
                              <BreakdownList items={row.card_breakdown} tone="primary" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </TableSection>
            ) : null}

            {tab === 1 ? (
              <TableSection
                eyebrow="Monthly View"
                title="Full Sale Report Per Month"
                description="Monthly totals are calculated by summing each day's sales, then splitting cash, card, online, and credit values."
              >
                {monthlyRows.length === 0 ? (
                  <EmptyState
                    title="No monthly sales found"
                    description="There are no monthly sales rows available for this selection."
                  />
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellSx}>Month</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Days</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Total Sales</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Cash</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Card</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Online</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Credit</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Tips</TableCell>
                          <TableCell align="right" sx={headerCellSx}>Service</TableCell>
                          <TableCell sx={headerCellSx}>Card Types</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {monthlyRows.map((row) => (
                          <TableRow key={row.key} hover sx={stripedRowSx}>
                            <TableCell sx={{ fontWeight: 700 }}>{row.label || formatSriLankaMonthLabel(row.key)}</TableCell>
                            <TableCell align="right">{row.day_count}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.total_sales)}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.cash_payments)}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.card_payments)}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.online_sales)}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.credit_sales)}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.total_tips)}</TableCell>
                            <TableCell align="right">Rs {fmtMoney(row.total_service_charges)}</TableCell>
                            <TableCell sx={{ minWidth: 300 }}>
                              <BreakdownList items={row.card_breakdown} tone="info" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </TableSection>
            ) : null}

            {tab === 2 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <StatCard
                  title="Gross Profit"
                  value={`Rs ${fmtMoney(dailyProfitTotals.gross_profit)}`}
                  caption="After credit-sales deduction, expenses, purchases, and 3% bank charge"
                  icon={<TrendingUpIcon />}
                  tone="success"
                />
                <StatCard
                  title="Net Profit"
                  value={`Rs ${fmtMoney(dailyProfitTotals.net_profit)}`}
                  caption={`After advances: Rs ${fmtMoney(dailyProfitTotals.advance_total)}`}
                  icon={<TrendingUpIcon />}
                  tone="primary"
                />
                <StatCard
                  title="Bank Charge"
                  value={`Rs ${fmtMoney(dailyProfitTotals.bank_charge)}`}
                  caption="3% of card and online payments"
                  icon={<RequestQuoteIcon />}
                  tone="warning"
                />
                <StatCard
                  title="Purchases + Expenses"
                  value={`Rs ${fmtMoney(dailyProfitTotals.purchase_expenses + dailyProfitTotals.operating_expenses)}`}
                  caption={`Operating: Rs ${fmtMoney(dailyProfitTotals.operating_expenses)}`}
                  icon={<RequestQuoteIcon />}
                    tone="error"
                  />
                </Box>

                <TableSection
                  eyebrow="Profit View"
                  title="Profit Daily Breakdown"
                  description="Daily operating profit based on gross sales minus credit sales, then plus settlements/other income, minus expenses, purchases, and bank charge."
                >
                  {profitRows.length === 0 ? (
                    <EmptyState
                      title="No profit rows found"
                      description="Profit details are not available for the selected month."
                    />
                  ) : (
                    <TableContainer sx={{ maxHeight: 680 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={headerCellSx}>Date</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Total Sales</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Credit Sales</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Recognized Sales</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Other Income</TableCell>
                            <TableCell sx={headerCellSx}>Other Income Details</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Expenses</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Purchases</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Bank Charge</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Advances</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Gross Profit</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Net Profit</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Electronic</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Cash on Cashier</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {profitRows.map((row) => (
                            <TableRow key={row.date} hover sx={stripedRowSx}>
                              <TableCell sx={{ fontWeight: 700 }}>{formatSriLankaDateLabel(row.date)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.total_sales)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.credit_sales)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.recognized_sales)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.other_income)}</TableCell>
                              <TableCell sx={{ minWidth: 260 }}>
                                <BreakdownList
                                  items={row.other_income_breakdown}
                                  emptyLabel="No other income details"
                                  tone="success"
                                />
                              </TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.operating_expenses)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.purchase_expenses)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.bank_charge)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.advance_total)}</TableCell>
                              <TableCell align="right" sx={{ color: 'success.dark', fontWeight: 700 }}>
                                Rs {fmtMoney(row.gross_profit)}
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 800 }}>
                                Rs {fmtMoney(row.net_profit)}
                              </TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.electronic_payments)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.cash_on_cashier)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </TableSection>
              </Box>
            ) : null}

            {tab === 3 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.info.main, 0.18),
                  }}
                >
                  Salary without advance = net salary + advance deduction. Salary month follows Sri Lanka time: {data?.filters?.salary_month_label || selectedMonthLabel}.
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <StatCard
                    title="Net Salary"
                    value={`Rs ${fmtMoney(salarySummary?.net_salary)}`}
                    caption={`${salarySummary?.total_staff || 0} staff in this month`}
                    icon={<PaymentsIcon />}
                    tone="primary"
                  />
                  <StatCard
                    title="Advance Deductions"
                    value={`Rs ${fmtMoney(salarySummary?.advance_deductions)}`}
                    caption="Advance salary amounts removed from payroll"
                    icon={<RequestQuoteIcon />}
                    tone="warning"
                  />
                  <StatCard
                    title="Salary Without Advance"
                    value={`Rs ${fmtMoney(salarySummary?.salary_without_advance)}`}
                    caption="Base view without advance deductions"
                    icon={<PaymentsIcon />}
                    tone="success"
                  />
                </Box>

                <TableSection
                  eyebrow="Salary View"
                  title="Salaries Excluding Advance Salaries"
                  description="Per-staff salary summary with net salary, advance deduction, and punished half-day balances moved to the organization fund."
                >
                  {salaryRows.length === 0 ? (
                    <EmptyState
                      title="No salary rows found"
                      description="Salary data is not available for the selected month."
                    />
                  ) : (
                    <TableContainer sx={{ maxHeight: 680 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={headerCellSx}>Staff</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Days</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Basic</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Extra</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Bonus</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Penalty</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Loan</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Advance</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Net Salary</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Without Advance</TableCell>
                            <TableCell align="center" sx={headerCellSx}>Settled</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {salaryRows.map((row) => (
                            <TableRow key={row.staff_id} hover sx={stripedRowSx}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {row.staff_name}
                                </Typography>
                                {Number(row.punishment_half_days || 0) > 0 ? (
                                  <Typography variant="caption" color="warning.main">
                                    Punish half: {row.punishment_half_days} | Fund: Rs {fmtMoney(row.punishment_half_day_fund)}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell align="right">{row.days_present}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.basic_earning)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.extra_earning)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.bonus_total)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.penalty_total)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.loan_deduction)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.advance_deduction)}</TableCell>
                              <TableCell align="right">Rs {fmtMoney(row.total_salary)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 800, color: 'success.dark' }}>
                                Rs {fmtMoney(row.salary_without_advance)}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  color={row.settled ? 'success' : 'default'}
                                  label={row.settled ? 'Settled' : 'Pending'}
                                  sx={{ fontWeight: 700 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </TableSection>
              </Box>
            ) : null}
          </>
        )}
      </Box>
    </Box>
  );
}
